/**
 * Projects whose site-plan 3D / 3DS modes are locked to ADMIN.
 *
 * Everywhere else every user gets both modes; on a listed project anyone else
 * falls back to 2D. Projects may be keyed by `Project.Id` or normalized name.
 *
 * Mirrored server-side in `backend/src/utils/sitePlan3dRestrictedProjects.ts`
 * (used by `requireAdminOrSitePlan3dSun`) — change both together.
 */
export const SITE_PLAN_3D_RESTRICTED_PROJECT_IDS: ReadonlySet<string> = new Set<string>([
  // Empty — Thomson Reserve (P2511010) was opened up to everyone.
]);

export const SITE_PLAN_3D_RESTRICTED_PROJECT_NAMES: ReadonlySet<string> = new Set<string>([
  // Empty — The Serra Residences, Aurea and Promenade Peak were opened up to everyone.
]);

/** True when the project is one of the ADMIN-only projects above. */
export function isSitePlan3dRestrictedProject(
  projectId?: string | number | null,
  projectName?: string | null,
): boolean {
  const normalizedId = projectId === null || projectId === undefined
    ? ''
    : String(projectId).trim().toUpperCase();
  const normalizedName = String(projectName ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  return SITE_PLAN_3D_RESTRICTED_PROJECT_IDS.has(normalizedId)
    || SITE_PLAN_3D_RESTRICTED_PROJECT_NAMES.has(normalizedName);
}

/**
 * Projects whose site-plan 3DS (solar) mode is held back from the general
 * audience. 3D stays open to everyone; only the sun study is withheld. ADMIN and
 * PROJECT-DEPT always get through, plus holders of the project's own grant role
 * (a `TransformationAppUserAccess` row with `App = SUPER_PICKER`). Keyed by
 * `Project.Id` → grant role. Mirrored in
 * `backend/src/utils/sitePlan3dRestrictedProjects.ts` — change both together.
 */
export const SITE_PLAN_3DS_LOCKED_PROJECT_ROLES: ReadonlyMap<string, string> = new Map<string, string>([
  ['P2511010', 'MAP-3DS-THOMSON'], // Thomson Reserve
]);

/**
 * The extra role that unlocks `projectId`'s 3DS mode, or null when the project
 * is not locked (3DS then follows the general site-plan grant).
 */
export function getSitePlan3dsLockedProjectRole(projectId?: string | number | null): string | null {
  if (projectId === null || projectId === undefined) return null;
  return SITE_PLAN_3DS_LOCKED_PROJECT_ROLES.get(String(projectId).trim().toUpperCase()) ?? null;
}
